import { SWRConfiguration, Cache } from "swr";
import { fetcher } from "./api";

/**
 * Cache global que persiste entre navegaciones.
 * Esto permite que los datos se mantengan en memoria incluso cuando los componentes
 * se desmontan y montan durante la navegación.
 */
const swrCache = new Map<string, any>();

/**
 * Provider personalizado de SWR para mantener el cache entre navegaciones.
 * SWR v2 requiere que el provider sea una función que retorne el cache.
 */
const swrProvider: Cache<any> = {
  get: (key: string) => {
    return swrCache.get(key);
  },
  set: (key: string, value: any) => {
    swrCache.set(key, value);
  },
  delete: (key: string) => {
    swrCache.delete(key);
  },
  keys: () => {
    return swrCache.keys();
  },
};

/**
 * Configuración optimizada de SWR para la aplicación.
 * Incluye:
 * - Provider personalizado para cache persistente
 * - Deduplicación de requests
 * - Revalidación optimizada
 * - Manejo de errores centralizado
 */
export const swrConfig: SWRConfiguration = {
  fetcher,
  provider: () => swrProvider,
  // Deduplicación: evita requests duplicados en un intervalo de 30 segundos (aumentado para evitar rate limiting)
  dedupingInterval: 30000,
  // Revalidación en focus: deshabilitado para rutas protegidas (SWR revalida automáticamente)
  revalidateOnFocus: false,
  // Revalidación en reconexión: habilitado para actualizar datos cuando se recupera la conexión
  revalidateOnReconnect: true,
  // Mantener datos anteriores durante la revalidación para transiciones suaves
  keepPreviousData: true,
  // No reintentar automáticamente en errores (el manejo de errores se hace en api.ts)
  shouldRetryOnError: (error) => {
    // No reintentar si es error 429 (Too Many Requests)
    if (error && typeof error === 'object' && 'status' in error) {
      return (error as { status: number }).status !== 429;
    }
    return false;
  },
  // Reducir número de reintentos
  errorRetryCount: 1,
  // Aumentar intervalo entre reintentos
  errorRetryInterval: 5000,
  // Tiempo de revalidación automática (opcional, puede ser configurado por hook)
  refreshInterval: 0,
  // Manejo de errores global
  onError: (error, key) => {
    // Los errores ya se manejan en api.ts con handleAuthError
    // Aquí solo logueamos para debugging si es necesario
    // No loguear errores 429 para reducir ruido en consola
    if (process.env.NODE_ENV === "development") {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status: number }).status;
        if (status !== 429) {
          console.error(`[SWR Error] Key: ${key}`, error);
        }
      } else {
        console.error(`[SWR Error] Key: ${key}`, error);
      }
    }
  },
  // Configuración de comparación de datos para evitar re-renders innecesarios
  compare: (a, b) => {
    // Comparación profunda simple (SWR usa shallow comparison por defecto)
    return JSON.stringify(a) === JSON.stringify(b);
  },
};

